import styled from "@emotion/styled";
import React from "react";

import Text from "@components/Text";
import { useLang, useSpeed } from "@src/hooks/useLang";
import {
  speedColor, moistureColor, moistureScale, SPEED_LAYER, MOISTURE_LAYER,
} from "@screens/TracksScreen/utils";

const Ramp = styled.div<{ gradient: string }>`
  height: 10px;
  margin: 6px 0 4px 0;
  border-radius: 3px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  background: ${({ gradient }) => gradient};
`;

const Ends = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
`;

// Samples along the ramp. The map's moisture ramp blends between stops, so a
// handful of samples reproduces it; speed snaps to stops and reads the same.
const SAMPLES = 9;

/**
 * Color legend for a value layer: the stretch of the map's ramp the data
 * actually covers, labelled with its minimum and maximum.
 *
 * `colorAt` is the same function the map paints with, so the bar's ends are
 * the colors of the lowest and highest point on the track.
 */
const ValueLegend: React.FC<{
  title: string;
  unit: string;
  min: number;
  max: number;
  decimals: number;
  colorAt: (value: number) => string;
}> = ({ title, unit, min, max, decimals, colorAt }) => {
  const lang = useLang();
  const stops = Array.from({ length: SAMPLES }, (_, i) => {
    const value = min + ((max - min) * i) / (SAMPLES - 1);
    return `${colorAt(value)} ${Math.round((i / (SAMPLES - 1)) * 100)}%`;
  });
  const format = (v: number) => `${v.toFixed(decimals)} ${unit}`;

  return (
    <div>
      <Text size={12} weight={500} color="#494C5B">{title}</Text>
      <Ramp gradient={`linear-gradient(to right, ${stops.join(", ")})`} />
      <Ends>
        <Text size={11} color="#666">
          {`${lang.fields.tracksSpeedMin ?? "Min"} ${format(min)}`}
        </Text>
        <Text size={11} color="#666" textAlign="right">
          {`${lang.fields.tracksSpeedMax ?? "Max"} ${format(max)}`}
        </Text>
      </Ends>
    </div>
  );
};

export default ValueLegend;

function rangeOf(values: (number | null)[]): { min: number; max: number } | null {
  let min = Infinity;
  let max = -Infinity;
  for (const v of values) {
    if (v == null || !isFinite(v)) continue;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  return isFinite(min) ? { min, max } : null;
}

/**
 * The legend for whichever of speed / grain moisture the map is painting;
 * nothing for the other layers (the sensor ramp lives in the filter card).
 *
 * `speedsKmh` / `moistures` are the readings to take the min and max over (the
 * day on the map, the zoomed window in the Details panel). The moisture ramp
 * itself is stretched over `moistureScaleOf` — the whole day, as the map does.
 */
export const LayerLegend: React.FC<{
  layer: string;
  speedsKmh: (number | null)[];
  moistures: (number | null)[];
  moistureScaleOf: (number | null)[];
}> = ({ layer, speedsKmh, moistures, moistureScaleOf }) => {
  const lang = useLang();
  const speed = useSpeed();

  if (layer === SPEED_LAYER) {
    const range = rangeOf(speedsKmh);
    if (!range) return null;
    return (
      <ValueLegend
        title={lang.tracks.speedLayerTitle}
        unit={speed.label}
        min={speed.fromKmh(range.min)}
        max={speed.fromKmh(range.max)}
        decimals={1}
        colorAt={(v) => speedColor(speed.toKmh(v))}
      />
    );
  }

  if (layer === MOISTURE_LAYER) {
    const range = rangeOf(moistures);
    if (!range) return null;
    const { lo, hi } = moistureScale(moistureScaleOf);
    return (
      <ValueLegend
        title={lang.tracks.moistureLayerTitle ?? "Grain moisture"}
        unit="%"
        min={range.min}
        max={range.max}
        decimals={1}
        colorAt={(v) => moistureColor(v, lo, hi)}
      />
    );
  }

  return null;
};
